// Rings stacked along the height axis — chapter 07's pinecone topology.
//
// The first substrate whose own z is not 0, which is what the stride-3
// migration was for: `polar` is rings in a plane, this is rings up an axis.
// The profile follows chapter 07 — a quarter-sine flare off the base, a
// half-sine bulge through the body, a linear taper, and a per-ring angular
// offset that produces the phyllotaxis spiral.
//
// Caveat worth knowing: fields are f(x, y, t), so a column samples the field
// by its horizontal cross-section and the field effectively extrudes up the
// axis. Rings at the same radius only differ because `spin` offsets them.
import { mulberry32 } from '../rng.js'

// Fraction of the column spent flaring out of the base before the bulge.
const BASE = 0.26

export default {
  id: 'column',
  label: 'Column',
  motion: 'resample',
  params: [
    { key: 'rings', label: 'rings', tier: 'structural', min: 4, max: 64, step: 1, default: 34 },
    { key: 'around', label: 'per ring', tier: 'structural', min: 3, max: 24, step: 1, default: 9 },
    { key: 'spin', label: 'ring offset', tier: 'structural', min: 0, max: 0.5, step: 0.005, default: 0.04,
      hint: 'turns per ring · the spiral' },
    { key: 'bulge', label: 'bulge', tier: 'structural', min: 0, max: 0.9, step: 0.01, default: 0.42 },
    { key: 'taper', label: 'taper', tier: 'structural', min: 0, max: 1, step: 0.02, default: 0.3 },
    { key: 'jitter', label: 'jitter', tier: 'structural', min: 0, max: 1, step: 0.02, default: 0 },
  ],

  build(cfg, seed) {
    const rnd = mulberry32(seed)
    const rings = Math.max(2, Math.round(cfg.rings))
    const around = Math.max(3, Math.round(cfg.around))
    const pts = new Float32Array(rings * around * 3)
    const runs = new Int32Array(rings)
    runs.fill(around)
    let i = 0
    for (let r = 0; r < rings; r++) {
      const u = r / (rings - 1)
      const core = u < BASE
        ? Math.sin((u / BASE) * Math.PI * 0.5) * 0.45
        : 0.45 + Math.sin(((u - BASE) / (1 - BASE)) * Math.PI) * cfg.bulge
      const rad = core * (1 - u * cfg.taper)
      const off = r * cfg.spin
      const z = u * 2 - 1
      for (let k = 0; k < around; k++) {
        const a = ((k / around) + off) * Math.PI * 2
        const j = 1 + (rnd() - 0.5) * cfg.jitter
        pts[i++] = Math.cos(a) * rad * j
        pts[i++] = Math.sin(a) * rad * j
        pts[i++] = z
      }
    }
    return { points: pts, count: rings * around, runs, closed: true }
  },
}
